import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NoticeList } from 'src/app/notice/model/noticeList';
import { Observable, Subscription } from 'rxjs';
import { NoticeEdit } from 'src/app/notice/model/noticeEdit';
import { TranslateService } from '@ngx-translate/core';
import { MessengerService } from './messenger.service';
import { UserNotification } from 'src/app/user/model/user-notification';

@Injectable({
  providedIn: 'root'
})
export class NoticeService implements OnDestroy
{

  public noticePriorities: Array<{ id: string; displayName: string; class: string }> = [
    { id: 'NORMAL', displayName: 'Normal', class: '' },
    { id: 'HIGH', displayName: 'High', class: 'red' }
  ];

  public notificationTypes: Array<{ text: string; value: string }> = [
    { text: 'Info', value: 'info' },
    { text: 'Success', value: 'success' },
    { text: 'Warning', value: 'warning' },
    { text: 'Error', value: 'error' },
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private messengerService: MessengerService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get notices for grid list.
   */
  public getNoticesList(): Observable<NoticeList[]>
  {
    return this.http.get<NoticeList[]>('/notices/list');
  }

  /**
   * Delete a notice.
   *
   * @param id Notice id
   */
  public deleteNotice(id: number): Observable<NoticeList>
  {
    return this.http.delete<NoticeList>(`/notice/${id}`);
  }

  /**
   * Get notice by id.
   *
   * @param id Notice id
   */
  public getNoticeById(id: number): Observable<NoticeEdit>
  {
    return this.http.get<NoticeEdit>(`/notice/${id}`);
  }

  /**
   * Save a notice.
   *
   * @param data Notice to persit
   * @param isNew Indicate if this is a new or update
   * @param noticeId Notice id to update.
   */
  public saveNotice(data: any, isNew: boolean, noticeId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/notice', data);
    }
    else
    {
      return this.http.put(`/notice/${noticeId}`, data);
    }
  }

  /**
   * Get notice dashboard.
   *
   * @param userId User id.
   * @param localDate Local date to retrive the notice.
   */
  public getNoticeDashboard(userId: number, localDate: string): Observable<NoticeEdit[]>
  {
    return this.http.get<NoticeEdit[]>(`/notices/dashboard?userId=${userId}&fromDate=${localDate}`);
  }

  /**
   * Get notice dashboard count.
   *
   * @param userId User id.
   * @param localDate Local date to retrive the notice.
   */
  public getNoticeDashboardCount(userId: number, localDate: string): Observable<NoticeEdit[]>
  {
    return this.http.get<NoticeEdit[]>(`/notices/dashboard?userId=${userId}&fromDate=${localDate}&countOnly=true`);
  }

  /**
   * Save a notice.
   *
   * @param userId User id.
   * @param noticeId Notice id to dismiss.
   */
  public dismissNotice(userId: number, noticeId: number): Observable<any>
  {
    return this.http.post(`/notice/${noticeId}/dismiss/user/${userId}`, null);
  }

  /**
   * Send a user notification.
   *
   * @param userNotification Notification object.
   */
  public sendNotification(userNotification: UserNotification): Observable<any>
  {
    return this.http.post(`/notification/send`, userNotification);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    this.translate.get(['NOTIFICATION-TYPES']).subscribe((res: string) =>
    {
      const splitted = res['NOTIFICATION-TYPES'].split(',');
      for (let i = 0; i < splitted.length; i++)
      {
        if (this.notificationTypes.length > i)
        {
          this.notificationTypes[i].text = splitted[i];
        }
      }
    });
    this.translate.get(['NOTICE-NORMAL', 'NOTICE-HIGH']).subscribe({
      next: (res) =>
      {
        let index = 0;
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            this.noticePriorities[index].displayName = res[key];
            index++;
          }
        }
      }
    });
  }
}
